/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-unsafe-optional-chaining */
import { makeAutoObservable } from 'mobx';
import isObject from 'lodash/isObject';
import {
  IProductColor,
  IProductColorSwatch,
  IProductFileImageType,
  IProductFileObject,
  IProductManufacturerObject,
  IProductPackshots,
  IProductPriceObject,
  IProductPricesObject,
  TProductFlagObject,
  TProductFlagsKey,
  IGpsrObject,
} from '@features/categories/typescript';
import {
  PRODUCT_ARRANGEMENT_PHOTO,
  PRODUCT_HOVER_PHOTO,
  PRODUCT_LIST_HOVER_OPENED_PACKSHOT,
  PRODUCT_LIST_VIDEO,
  PRODUCT_MAIN_PHOTO,
  PRODUCT_MAIN_PHOTO_WITH_SHADOW,
  PRODUCT_MAIN_VIDEO,
} from '@features/categories/const';
import formatPrice from '@utils/formatPrice';
import { ISwatch } from '@typescript/index';
import { IS_SHOP } from '@config/index';
import getOffer from '@utils/getOffer';

class ProductState {
  public id: number;

  public name: string;

  public slug: string;

  public manufacturer: IProductManufacturerObject;

  public files: IProductFileObject[];

  public flags: TProductFlagObject[];

  public configurable: boolean;

  public colorSwatch: IProductColorSwatch;

  private price: IProductPriceObject;

  private prices: IProductPricesObject;

  private basePrice: IProductPriceObject;

  private currentPrice: IProductPriceObject;

  private omnibusPrice: IProductPriceObject;

  public index: number;

  public uuid: string;

  public features: any = undefined;

  private rap: any = undefined;

  public priceUnit: string;

  public gpsr: IGpsrObject | undefined = undefined;

  constructor(product) {
    this.id = product.id;
    this.name = product.name;
    this.slug = product.slug;
    this.manufacturer = product.manufacturer;
    this.files = product.files;
    this.flags = product.flags;
    this.price = product.price;
    this.prices = product.prices;
    this.basePrice = product.basePrice;
    this.currentPrice = product.currentPrice;
    this.omnibusPrice = product.omnibusPrice;
    this.configurable = product.configurable;
    this.colorSwatch = product.colorSwatch;
    this.index = product.index ?? 0;
    this.uuid = product.uuid ?? undefined;
    this.priceUnit = product.priceUnit ?? undefined;
    this.gpsr = product.gpsr ?? undefined;

    if (product.features) {
      this.features = product.features;
    }

    if (product.rap) {
      this.rap = product.rap;
    }
    makeAutoObservable(this);
  }

  public checkFlagActive = (flagName: TProductFlagsKey): boolean => {
    const { flags } = this;

    const selectedFlag = flags && flags.find((flag) => flag.key === flagName);
    if (!selectedFlag) return false;
    return selectedFlag.value === true;
  };

  public get colors(): Array<IProductColor> {
    const arrayOfColors: Array<IProductColor> = [];

    if (this.features?.FEATURE__COLOR_MAIN) {
      const colorsObject = this.features.FEATURE__COLOR_MAIN as Record<string, IProductColor>;
      if (colorsObject) Object.keys(colorsObject).forEach((colorKey) => arrayOfColors.push(colorsObject[colorKey]));

      return arrayOfColors;
    }
    if (
      !this.colorSwatch ||
      !this.colorSwatch.featured ||
      !this.colorSwatch.totalCount ||
      this.colorSwatch.totalCount === 0
    )
      return [];

    this.colorSwatch.featured.forEach((color) => {
      arrayOfColors.push({
        featureValue: color.rawFeatureValueCode,
        featureValueDescriptive: color.rawFeatureValueName,
        featureName: color.rawFeatureValueName,
        featureKey: color.rawFeatureValueCode,
        value: color.marketingColors.length ? color.marketingColors[0].hex : '',
        featureImageUrl: color.rawFeatureValueFile,
      });
    });

    return arrayOfColors;
  }

  public get colorsCount(): number {
    // if (!this.colorSwatch?.totalCount) return 0;

    if (this.colorSwatch?.totalCount) {
      return this.colorSwatch.totalCount;
    }

    if (this.colors) {
      return this.colors.length;
    }

    return 0;
  }

  public getPrice = (priceListName: string): string => {
    if (IS_SHOP !== 'true') {
      return '';
    }

    if (this.prices) {
      const currentOfferPrice = this.prices?.currentPrice?.value;
      const baseOfferPrice = this.prices?.basePrice?.value;
      const currency = this.prices?.basePrice?.currency;
      if (!currentOfferPrice || !baseOfferPrice) return '';
      const parsedPrice = baseOfferPrice > currentOfferPrice ? baseOfferPrice : currentOfferPrice;
      return `${formatPrice(parsedPrice * 100, currency)}`;
    }

    if (this.rap) {
      const [offerName] = getOffer();
      const { rap, price } = this;
      const currency = rap?.prices[offerName]?.prices[priceListName]
        ? rap.prices[offerName].prices[priceListName].currency
        : 'MXN';
      if (!rap?.prices[offerName]?.prices[priceListName]) {
        if (isObject(price) && price[offerName]?.gross.amount) {
          return `${price[offerName].gross.amount} ${currency}`;
        }
        return '';
      }

      const { current, base } = rap?.prices[offerName]?.prices[priceListName];
      if (!current || !base) return '';

      const { amount: currentOfferPrice } = current.gross;
      const { amount: baseOfferPrice } = base.gross;

      const parsedPrice = baseOfferPrice > currentOfferPrice ? baseOfferPrice : currentOfferPrice;
      return `${formatPrice(parsedPrice * 100, currency)}`;
    }

    const { price, currentPrice, basePrice } = this;

    if (!price || !currentPrice || !basePrice) {
      return '';
    }

    if (
      !price.priceList.includes(priceListName) ||
      !currentPrice.priceList.includes(priceListName) ||
      !basePrice.priceList.includes(priceListName)
    ) {
      return '';
    }

    const { currency } = price;

    const currentOfferPrice = currentPrice.value;
    const baseOfferPrice = basePrice.value;
    const parsedPrice = baseOfferPrice > currentOfferPrice ? baseOfferPrice : currentOfferPrice;
    return `${formatPrice(parsedPrice * 100, currency)}`;
  };

  public getPromoPrice = (priceListName: string): string => {
    if (IS_SHOP !== 'true') {
      return '';
    }

    if (this.prices) {
      const currentOfferPrice = this.prices?.currentPrice?.value;
      const baseOfferPrice = this.prices?.basePrice?.value;
      const currency = this.prices?.basePrice?.currency;
      if (!currentOfferPrice || !baseOfferPrice) return '';
      const parsedPrice = formatPrice(currentOfferPrice * 100, currency);
      return currentOfferPrice < baseOfferPrice ? parsedPrice : '';
    }

    if (this.rap) {
      const [offerName] = getOffer();

      const { rap } = this;
      const currency = rap?.prices[offerName]?.prices[priceListName]
        ? rap.prices[offerName].prices[priceListName].currency
        : 'MXN';

      if (!rap?.prices[offerName]?.prices[priceListName]) return '';
      const { current, base } = rap?.prices[offerName]?.prices[priceListName];
      if (!current || !base) return '';

      const { amount: currentOfferPrice } = current.gross;
      const { amount: baseOfferPrice } = base.gross;

      const parsedPrice = formatPrice(currentOfferPrice * 100, currency);
      return currentOfferPrice < baseOfferPrice ? parsedPrice : '';
    }

    const { price, currentPrice, basePrice } = this;

    if (!price || !currentPrice || !basePrice) {
      return '';
    }

    if (
      price.priceList !== priceListName ||
      currentPrice.priceList !== priceListName ||
      basePrice.priceList !== priceListName
    ) {
      return '';
    }

    const { currency } = price;

    const currentOfferPrice = currentPrice.value;
    const baseOfferPrice = basePrice.value;

    const parsedPrice = formatPrice(currentOfferPrice * 100, currency);
    return currentOfferPrice < baseOfferPrice ? parsedPrice : '';
  };

  public getPromoPercent = (priceListName: string): string => {
    if (IS_SHOP !== 'true') {
      return '';
    }

    if (this.prices) {
      const currentOfferPrice = this.prices?.currentPrice?.value;
      const baseOfferPrice = this.prices?.basePrice?.value;
      if (!currentOfferPrice || !baseOfferPrice) return '';
      return currentOfferPrice < baseOfferPrice
        ? `-${Math.round(100 - (currentOfferPrice / baseOfferPrice) * 100)}%`
        : '';
    }

    if (this.rap) {
      const [offerName] = getOffer();

      const { rap } = this;

      if (!rap?.prices[offerName]?.prices[priceListName]) return '';
      const { current, base } = rap?.prices[offerName]?.prices[priceListName];
      if (!current || !base) return '';

      const { amount: currentOfferPrice } = current.gross;
      const { amount: baseOfferPrice } = base.gross;

      return currentOfferPrice < baseOfferPrice
        ? `-${Math.round(100 - (currentOfferPrice / baseOfferPrice) * 100)}%`
        : '';
    }

    const { price, currentPrice, basePrice } = this;

    if (!price || !currentPrice || !basePrice) {
      return '';
    }

    const currentOfferPrice = currentPrice.value;
    const baseOfferPrice = basePrice.value;

    return currentOfferPrice < baseOfferPrice
      ? `-${Math.round(100 - (currentOfferPrice / baseOfferPrice) * 100)}%`
      : '';
  };

  public getOmnibusPrice = (priceListName: string): string => {
    if (IS_SHOP !== 'true') {
      return '';
    }

    if (this.prices) {
      const currentOfferOmnibusPrice = this.prices?.omnibusPrice?.value;
      if (currentOfferOmnibusPrice) {
        const currency = this.prices?.basePrice?.currency;
        return `${formatPrice(currentOfferOmnibusPrice * 100, currency)}`;
      }
    }

    if (this.rap) {
      const [offerName] = getOffer();

      const { rap } = this;
      const currency = rap?.prices[offerName]?.prices[priceListName]
        ? rap.prices[offerName].prices[priceListName].currency
        : 'MXN';

      if (!rap?.prices[offerName]?.prices[priceListName]) return '';
      const { omnibus, base } = rap?.prices[offerName]?.prices[priceListName];
      if (!base) return '';

      if (!omnibus) {
        const { amount: baseOfferPrice } = base.gross;
        const parsedPrice = formatPrice(baseOfferPrice * 100, currency);
        return parsedPrice;
      }
      const { amount: omnibusOfferPrice } = omnibus.gross;

      const parsedPrice = formatPrice(omnibusOfferPrice * 100, currency);
      return omnibusOfferPrice && currency === 'PLN' ? parsedPrice : '';
    }

    const { price, omnibusPrice, basePrice } = this;

    if (!price || !omnibusPrice || !basePrice) {
      return '';
    }

    if (
      price.priceList !== priceListName ||
      omnibusPrice.priceList !== priceListName ||
      basePrice.priceList !== priceListName
    ) {
      return '';
    }

    const { currency } = price;

    if (!omnibusPrice) {
      const { value: baseOfferPrice } = basePrice;
      const parsedPrice = formatPrice(baseOfferPrice * 100, currency);
      return parsedPrice;
    }
    const { value: omnibusOfferPrice } = omnibusPrice;

    const parsedPrice = formatPrice(omnibusOfferPrice * 100, currency);
    return omnibusOfferPrice && (currency === 'PLN' || currency === 'EUR') ? parsedPrice : '';
  };

  public getFromFlag = (priceListName: string): boolean => {
    if (IS_SHOP !== 'true') {
      return false;
    }

    if (this.prices) {
      return this.prices.from ?? false;
    }

    if (this.rap) {
      const [offerName] = getOffer();

      const { rap } = this;
      return rap?.prices[offerName]?.prices[priceListName] ? rap?.prices[offerName]?.prices[priceListName].from : false;
    }

    const { price } = this;

    if (!price || priceListName !== price.priceList) {
      return false;
    }

    return price.from ?? false;
  };

  public get packshots() {
    const packshotsObj: IProductPackshots = {
      mainShadow: [],
      mainVideo: [],
      main: [],
      arrangement: [],
      hover: [],
      hoverOpened: [],
      hoverVideo: [],
    };
    if (!this.files) return packshotsObj;

    this.files.forEach((file) => {
      if (file.file?.base) {
        const obj: IProductFileObject = {
          code: file.type as IProductFileImageType,
          type: file.file.group === 'PHOTO' ? 'IMAGE' : 'VIDEO',
          url: file.file.base,
        };

        switch (file.type as string) {
          case 'PRODUCT_ARRANGEMENT':
            packshotsObj.arrangement.push(obj);
            break;
          case 'PRODUCT_MAIN':
            packshotsObj.main.push(obj);
            break;
          case 'PRODUCT_LIST_HOVER':
            packshotsObj.hover.push(obj);
            break;
          case 'PRODUCT_MAIN_WITH_SHADOW':
            packshotsObj.mainShadow.push(obj);
            break;
          case 'PRODUCT_VIDEO_PACKSHOT':
            packshotsObj.mainVideo.push(obj);
            break;
          case 'PRODUCT_LIST_VIDEO':
            packshotsObj.hoverVideo.push(obj);
            break;
          case 'PRODUCT_LIST_HOVER_OPENED_PACKSHOT':
            packshotsObj.hoverOpened.push(obj);
            break;
          default:
            break;
        }
      }

      switch (file.code) {
        case PRODUCT_ARRANGEMENT_PHOTO:
          packshotsObj.arrangement.push(file);
          break;
        case PRODUCT_MAIN_PHOTO:
          packshotsObj.main.push(file);
          break;
        case PRODUCT_HOVER_PHOTO:
          packshotsObj.hover.push(file);
          break;
        case PRODUCT_MAIN_PHOTO_WITH_SHADOW:
          packshotsObj.mainShadow.push(file);
          break;
        case PRODUCT_MAIN_VIDEO:
          packshotsObj.mainVideo.push(file);
          break;
        case PRODUCT_LIST_VIDEO:
          packshotsObj.hoverVideo.push(file);
          break;
        case PRODUCT_LIST_HOVER_OPENED_PACKSHOT:
          packshotsObj.hoverOpened.push(file);
          break;
        default:
          break;
      }
    });

    return packshotsObj;
  }

  public get swatches() {
    if (!this.colors || !this.colors.length) return [];

    return this.colors.map(
      (color): ISwatch => ({
        shape: 'circle',
        active: false,
        hoverable: false,
        size: 'small',
        image: color.featureImageUrl || '',
        color: color.value || undefined,
      }),
    );
  }

  public get gpsrObject(): IGpsrObject | undefined {
    return this.gpsr ?? undefined;
  }
}

export default ProductState;
